import { CSVReportResponse } from "services/types/reports.type";
import {
  SmsDeliveryFailuresDownloadQuery,
  SmsDeliveryFailuresListResponse,
  SmsDeliveryFailuresOverview,
  SmsDeliveryFailuresQueryParams,
  SmsDeliveryFailuresSummary
} from "services/types/sms-delivery-failures.type";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { emptySplitApi } from "./empty-split-api";

/**
 * Texts not delivered (TEXTS_NOT_DELIVERED_FEATURE, Intercom 215476575290017). Nothing here
 * may be called with the flag off: the tab, the panel and the thread alert all mount behind it.
 * Reads are practice-wide on purpose (the clinic filter is the only clinic narrowing).
 *
 * Every read provides `SmsDeliveryFailures` AND `PatientDialogs`, like the unanswered-texts
 * reads: each existing dialog-list invalidation (a staff reply, mark read, socket refreshes)
 * refreshes these too. `markSmsFailureHandled` invalidates both plus the patient.
 */

export const DEFAULT_SMS_DELIVERY_FAILURES_LIMIT = 25;

export const smsDeliveryFailuresApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getSmsDeliveryFailures: builder.query<
      SmsDeliveryFailuresListResponse,
      SmsDeliveryFailuresQueryParams
    >({
      query: (params) => ({
        url: "/sms-delivery-failures/",
        method: "GET",
        params: {
          ...params,
          limit: params.limit ?? DEFAULT_SMS_DELIVERY_FAILURES_LIMIT,
          offset: params.offset ?? 0
        }
      }),
      providesTags: ["SmsDeliveryFailures", "PatientDialogs"]
    }),
    getSmsDeliveryFailuresSummary: builder.query<
      SmsDeliveryFailuresSummary,
      SmsDeliveryFailuresQueryParams
    >({
      query: ({ limit: _limit, offset: _offset, ...filters }) =>
        `/sms-delivery-failures/summary/${formatValidQueryParamUrlCommaSeparated(
          filters,
          true
        )}`,
      providesTags: ["SmsDeliveryFailures", "PatientDialogs"]
    }),
    // Practice-wide by design: the tab badge and the By clinic breakdown.
    getSmsDeliveryFailuresOverview: builder.query<
      SmsDeliveryFailuresOverview,
      void
    >({
      query: () => "/sms-delivery-failures/overview/",
      providesTags: ["SmsDeliveryFailures", "PatientDialogs"]
    }),
    markSmsFailureHandled: builder.mutation<
      { patient_id: number; handled_at: string },
      { patientId: number }
    >({
      query: ({ patientId }) => ({
        url: `/sms-delivery-failures/${patientId}/handled/`,
        method: "POST"
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        "SmsDeliveryFailures",
        "PatientDialogs",
        { type: "Patient", id: patientId }
      ]
    }),
    downloadSmsDeliveryFailuresCSV: builder.query<
      CSVReportResponse,
      SmsDeliveryFailuresDownloadQuery
    >({
      query: (filters) => ({
        url: `/sms-delivery-failures/${formatValidQueryParamUrlCommaSeparated(
          filters,
          true
        )}`,
        headers: { Accept: "custom-csv/json" }
      })
    })
  })
});

export const {
  useGetSmsDeliveryFailuresQuery,
  useGetSmsDeliveryFailuresSummaryQuery,
  useGetSmsDeliveryFailuresOverviewQuery,
  useMarkSmsFailureHandledMutation,
  useLazyDownloadSmsDeliveryFailuresCSVQuery
} = smsDeliveryFailuresApi;
